import {
  MessageBar,
  MessageBarType,
  Panel,
  PanelType,
  PivotItem,
  PrimaryButton,
  SearchBox,
} from '@fluentui/react';
import * as React from 'react';
import { useState } from 'react';

import { PrivacyApprovalArtifact } from '../../../../Common/CommonModels/Attachments';
import { ViewPrivacyApprovalArtifacts } from '../../../../Common/Components/ViewPrivacyApprovalArtifacts';
import { SubscriberRequestRecord } from '../../../../Common/ConsumerModels/SubscriberRequestRecord';
import * as Styled from '../../../../Common/Styles/viewRequest.styled';
import { useViewSubscriptionAttributes } from '../../../../Hooks/Consumer/useViewSubscriptionAttributes';
import { ViewAttributeSubscriptionsForm } from './ViewAttributeSubscriptionsForm';

type ViewSubscriberRequestPanelProps = {
  isOpen: boolean;
  dismissPanel(): void;
  subscriberRequest: SubscriberRequestRecord;
};

export function ViewSubscriberRequestPanel(props: ViewSubscriberRequestPanelProps): React.ReactElement {
  const { isOpen, dismissPanel, subscriberRequest } = props;
  const [selectedPivotKey, setSelectedPivotKey] = React.useState('BasicDetails');

  // Subscriber Request State
  const [name] = useState<string>(subscriberRequest.name);
  const [description] = useState<string>(subscriberRequest.description);
  const [environment] = useState<number>(subscriberRequest.environment);
  const [id] = useState<string>(subscriberRequest.id);
  const [egressOptions, setEgressOptions] = useState<string>('');
  const [serviceBusNamespace] = useState<string>(subscriberRequest.serviceBusNamespace);
  const [serviceBusTopic] = useState<string>(subscriberRequest.serviceBusTopic);
  const [effectiveDatedCDM] = useState<string>(subscriberRequest.effectiveDatedCDM);
  const [attachmentList] = React.useState<PrivacyApprovalArtifact[]>(subscriberRequest.privacyApprovalArtifacts);
  const [privacyApprovalDate] = React.useState<Date | undefined>(new Date(subscriberRequest.privacyApprovalDate));
  const [additionalNotes] = useState<string>(subscriberRequest.additionalNotes);
  const [privacyTeamName] = useState<string>(subscriberRequest.privacyTeamName);
  // Attribute Subscription State
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const [
    formattedApprovedAttributes,
    setFormattedApprovedAttributes,
    filteredApprovedAttributes,
    setFilteredApprovedAttributes,
    isApprovedAttributesLoading,
  ] = useViewSubscriptionAttributes(subscriberRequest.subscribedAttributes, searchQuery);

  React.useEffect(() => {
    const egressOptionStringList: string[] = [];
    subscriberRequest.egressOptions.forEach((egressOption) => {
      if (egressOption === 1) {
        egressOptionStringList.push('API');
      } else if (egressOption === 2) {
        egressOptionStringList.push('Service Bus');
      } else if (egressOption === 3) {
        egressOptionStringList.push('DataLake');
      } else if (egressOption === 4) {
        egressOptionStringList.push('Kusto');
      } else if (egressOption === 5) {
        egressOptionStringList.push('GraphQL Application');
      } else if (egressOption === 6) {
        egressOptionStringList.push('GraphQL Delegated');
      } else if (egressOption === 7) {
        egressOptionStringList.push('Snapshot');
      }
    });
    const newEgressOptions = egressOptionStringList.join(', ');
    setEgressOptions(newEgressOptions);
  }, [subscriberRequest]);

  const onChangeSearchBoxQuery = (newSearchQuery: string) => {
    setSearchQuery(newSearchQuery);
  };

  const onChangeSearchBoxClear = () => {
    setSearchQuery('');
  };

  const handlePivotChange = (item: PivotItem) => {
    setSelectedPivotKey(item.props.itemKey);
  };

  const onRenderHeader = () => {
    return (
      <Styled.PanelHeaderContainer>

        <MessageBar messageBarType={MessageBarType.warning}>This view is read only.</MessageBar>
        <Styled.PanelHeaderTitleText>View Subscriber Request</Styled.PanelHeaderTitleText>

        <Styled.PivotStyled
          aria-label="View Subscriber Request Panel Pivot"
          selectedKey={selectedPivotKey}
          onLinkClick={handlePivotChange}
          defaultSelectedKey="BasicDetails"
        >
          <PivotItem headerText="Basic Details" itemKey="BasicDetails" />
          <PivotItem headerText="Attribute Subscriptions" itemKey="AttributeSubscriptions" />
        </Styled.PivotStyled>

        <Styled.HeaderDivider />

        {selectedPivotKey === 'AttributeSubscriptions' && (
          <Styled.SearchBoxContainer>
            <SearchBox
              placeholder="Search and Press Enter"
              aria-label="Search for Attribute Subscriptions"
              defaultValue={searchQuery}
              onSearch={(newSearchQuery) => onChangeSearchBoxQuery(newSearchQuery)}
              onClear={onChangeSearchBoxClear}
            />
          </Styled.SearchBoxContainer>
        )}

      </Styled.PanelHeaderContainer>
    );
  };

  const onRenderFooterContent = () => {
    return <PrimaryButton text="Close" onClick={dismissPanel} />;
  };

  const onRenderNavigationEmptyContent = () => {
    return <div></div>;
  };

  return (
    <Panel
      isOpen={isOpen}
      type={PanelType.medium}
      onDismiss={dismissPanel}
      onRenderHeader={onRenderHeader}
      onRenderFooterContent={onRenderFooterContent}
      onRenderNavigation={onRenderNavigationEmptyContent}
      hasCloseButton={false}
      isFooterAtBottom={true}
      isLightDismiss={true}
      styles={Styled.stickyFooterPanelStyles}
      data-testid="view-subscriber-request-panel"
    >
      {selectedPivotKey === 'BasicDetails' && (

        <Styled.ViewRequestContainer>

          <Styled.BasicDetailsContainer>

            <Styled.BasicDetailsSection>
              <Styled.BasicDetailsField>Request ID</Styled.BasicDetailsField>
              <Styled.BasicDetailsValue>{id}</Styled.BasicDetailsValue>
            </Styled.BasicDetailsSection>

            <Styled.BasicDetailsSection>
              <Styled.BasicDetailsField>Environment</Styled.BasicDetailsField>
              <Styled.BasicDetailsValue>{environment === 0 ? 'Non-Production' : 'Production'}</Styled.BasicDetailsValue>
            </Styled.BasicDetailsSection>

            <Styled.BasicDetailsSection>
              <Styled.BasicDetailsField>Request Name</Styled.BasicDetailsField>
              <Styled.BasicDetailsValue>{name}</Styled.BasicDetailsValue>
            </Styled.BasicDetailsSection>

            <Styled.BasicDetailsSection>
              <Styled.BasicDetailsField>Description</Styled.BasicDetailsField>
              <Styled.BasicDetailsValue>{description.length > 0 ? description : 'N/A'}</Styled.BasicDetailsValue>
            </Styled.BasicDetailsSection>

            <Styled.BasicDetailsSection>
              <Styled.BasicDetailsField>Egress Options</Styled.BasicDetailsField>
              <Styled.BasicDetailsValue>{egressOptions}</Styled.BasicDetailsValue>
            </Styled.BasicDetailsSection>

            <Styled.BasicDetailsSection>
              <Styled.BasicDetailsField>Service Bus Namespace</Styled.BasicDetailsField>
              <Styled.BasicDetailsValue>{serviceBusNamespace.length > 0 ? serviceBusNamespace : 'N/A'}</Styled.BasicDetailsValue>
            </Styled.BasicDetailsSection>

            <Styled.BasicDetailsSection>
              <Styled.BasicDetailsField>Service Bus Topic</Styled.BasicDetailsField>
              <Styled.BasicDetailsValue>{serviceBusTopic.length > 0 ? serviceBusTopic : 'N/A'}</Styled.BasicDetailsValue>
            </Styled.BasicDetailsSection>

            <Styled.BasicDetailsSection>
              <Styled.BasicDetailsField>Effective-Dated CDM</Styled.BasicDetailsField>
              <Styled.BasicDetailsValue>{effectiveDatedCDM}</Styled.BasicDetailsValue>
            </Styled.BasicDetailsSection>

            <Styled.BasicDetailsSection>
              <Styled.BasicDetailsField>Authorized Privacy Team</Styled.BasicDetailsField>
              <Styled.BasicDetailsValue>{privacyTeamName === null ? 'N/A' : privacyTeamName}</Styled.BasicDetailsValue>
            </Styled.BasicDetailsSection>

            <Styled.BasicDetailsSection>
              <Styled.BasicDetailsField>Privacy Approval Date</Styled.BasicDetailsField>
              <Styled.BasicDetailsValue>{privacyApprovalDate.toLocaleDateString()}</Styled.BasicDetailsValue>
            </Styled.BasicDetailsSection>

            <Styled.BasicDetailsSection>
              <Styled.BasicDetailsField>Additional Notes</Styled.BasicDetailsField>
              <Styled.BasicDetailsValue>{additionalNotes.length > 0 ? additionalNotes : 'N/A'}</Styled.BasicDetailsValue>
            </Styled.BasicDetailsSection>

            <Styled.BasicDetailsSection>
              <Styled.BasicDetailsField>Privacy Approval Artifacts</Styled.BasicDetailsField>

              {attachmentList.length > 0 && (
                <ViewPrivacyApprovalArtifacts attachmentList={attachmentList} />
              )}

              {attachmentList.length == 0 && (
                <Styled.BasicDetailsValue>{'N/A'}</Styled.BasicDetailsValue>
              )}

            </Styled.BasicDetailsSection>

          </Styled.BasicDetailsContainer>

        </Styled.ViewRequestContainer>
      )}

      {selectedPivotKey === 'AttributeSubscriptions' && (
        <ViewAttributeSubscriptionsForm
          formattedApprovedAttributes={formattedApprovedAttributes}
          setFormattedApprovedAttributes={setFormattedApprovedAttributes}
          filteredApprovedAttributes={filteredApprovedAttributes}
          setFilteredApprovedAttributes={setFilteredApprovedAttributes}
          isApprovedAttributesLoading={isApprovedAttributesLoading}
        />
      )}

    </Panel>
  );
}
